import React, { useMemo, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { ChevronDown, Layers, ExternalLink, Copy, Check } from "lucide-react"
import TourCard from "../TourCard"
import { groupTours } from "../../utils/parsers/tourse"
import { formatLocalizedDate, money } from "../../utils/parsers/common"
import { useTranslation } from "react-i18next"

const buildSessionUrl = (sessionKey) =>
  `${window.location.origin}/book/saved_tours/session/${sessionKey}`

const SessionAccordion = ({ session, sort, showPhotos, defaultOpen = false }) => {
  const { t, i18n } = useTranslation()

  const [open, setOpen] = useState(defaultOpen)
  const [copied, setCopied] = useState(false)

  const sorted = useMemo(() => {
    const grouped = groupTours(session.tours)
    const stars = (g) => parseInt(g.star_name, 10) || 0
    const arr = [...grouped]
    if (sort === "price_asc") arr.sort((a, b) => a.minPrice - b.minPrice)
    if (sort === "price_desc") arr.sort((a, b) => b.minPrice - a.minPrice)
    if (sort === "stars_desc") arr.sort((a, b) => stars(b) - stars(a))
    return arr
  }, [session.tours, sort])

  const minPrice = sorted.length ? Math.min(...sorted.map((t) => t.minPrice)) : null
  const currency = sorted[0]?.currency
  const sessionUrl = buildSessionUrl(session.session_key)

  const handleCopyLink = async (e) => {
    e.stopPropagation()
    try {
      await navigator.clipboard.writeText(sessionUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {}
  }

  return (
    <div className="bg-white rounded-2xl border border-[#E7EAEF] overflow-hidden">
      <div className="w-full flex items-center justify-between gap-4 px-5 py-4 hover:bg-[#F8F9FB] transition-colors">
        <button
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-3 min-w-0 flex-1 text-left"
        >
          <ChevronDown
            size={16}
            className={`shrink-0 text-[#9AA5B4] transition-transform ${open ? "rotate-180" : ""}`}
          />
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-[#26303D]">
                {formatLocalizedDate(session.created_at, i18n.language)}
              </span>
              {session.temporary ? (
                <span className="text-[10px] uppercase tracking-wide font-medium px-2 py-0.5 rounded-full bg-[#FFF4E5] text-[#E8871E]">
                  draft
                </span>
              ) : (
                <span className="text-[10px] uppercase tracking-wide font-medium px-2 py-0.5 rounded-full bg-[#E9F7EF] text-[#1E9E5B]">
                  confirmed
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 text-xs text-[#9AA5B4] mt-0.5">
              <span className="inline-flex items-center gap-1">
                <Layers size={12} /> {session.tours_count} tour{session.tours_count > 1 ? "s" : ""}
              </span>
              <span className="font-mono truncate max-w-[160px]">{session.session_key}</span>
            </div>
          </div>
        </button>

        <div className="flex items-center gap-4 shrink-0">
          {!session.temporary && (
            <div className="flex items-center gap-1.5">
              <button
                onClick={handleCopyLink}
                title="Copy link"
                className="p-2 rounded-lg text-[#9AA5B4] hover:text-[#26303D] hover:bg-[#EEF1F5] transition-colors"
              >
                {copied ? <Check size={17} className="text-[#1E9E5B]" /> : <Copy size={17} />}
              </button>

              <a
                href={sessionUrl}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                title="Open session"
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-[#5A6779] hover:text-[#26303D] hover:bg-[#F4F6F9] transition-colors"
              >
                <ExternalLink size={15} />
                <span>Open</span>
              </a>
            </div>
          )}

          {minPrice != null && (
            <div className="text-right">
              <div className="text-[11px] text-[#9AA5B4]">from</div>
              <div className="text-sm font-mono font-semibold text-[#26303D]">
                {money(minPrice, currency)}
              </div>
            </div>
          )}
        </div>
      </div>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="flex flex-col gap-4 p-4 pt-0">
              {sorted.map((tour, i) => (
                <TourCard
                  key={tour.id ?? tour.price_id}
                  tour={tour}
                  index={i}
                  showPhotos={showPhotos}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default SessionAccordion
